import { Injectable } from '@angular/core';
import {
  HttpTransportType,
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
} from '@microsoft/signalr';
import { EMPTY, Observable, Subject, asyncScheduler, merge } from 'rxjs';
import { filter, map, throttleTime } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

interface DeviceWatch {
  serialNumber: string;
  watchers: number;
}

/**
 * Live "something changed, refetch" signals pushed by the P&D service over SignalR.
 *
 * The hub never carries data — only a hint that notifications or a device's telemetry changed.
 * Consumers react by calling the same REST APIs they already use, so authorization and data
 * shaping stay in one place.
 *
 * The connection is opened when the first consumer subscribes and closed when the last one
 * unsubscribes, so nothing is held open on the login page or after the shell is torn down.
 */
@Injectable({ providedIn: 'root' })
export class RealtimeService {
  // The hub is served by the P&D API host, next to (not under) its /api prefix.
  private readonly hubUrl = `${environment.apiUrl.replace(/\/api\/?$/, '')}/hubs/updates`;

  // A burst of events for the same screen (several packets, status + telemetry) should cause
  // one refetch now and at most one more afterwards, not one per event.
  private readonly refreshThrottleMs = 2000;
  private readonly maxRetryDelayMs = 30000;

  private connection: HubConnection | null = null;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private consumers = 0;
  private readonly deviceWatches = new Map<string, DeviceWatch>();

  private readonly notificationsChangedSubject = new Subject<void>();
  private readonly telemetryUpdatedSubject = new Subject<string>();
  // Fired after the connection comes back from an outage: events may have been missed while it
  // was down, so every consumer refetches once.
  private readonly resyncSubject = new Subject<void>();

  /** Emits whenever the signed-in user's notifications should be refetched. */
  notificationsChanged(): Observable<void> {
    return this.whileConnected(merge(this.notificationsChangedSubject, this.resyncSubject));
  }

  /** Emits whenever telemetry/status for any of the given devices should be refetched. */
  watchDevices(serialNumbers: string[]): Observable<void> {
    const serials = new Map<string, string>();
    for (const serialNumber of serialNumbers ?? []) {
      const trimmed = String(serialNumber ?? '').trim();
      if (trimmed && !serials.has(this.deviceKey(trimmed))) {
        serials.set(this.deviceKey(trimmed), trimmed);
      }
    }

    if (serials.size === 0) {
      return EMPTY;
    }

    const updates = new Observable<void>((subscriber) => {
      this.addDeviceWatches(serials);
      const subscription = merge(
        this.telemetryUpdatedSubject.pipe(
          filter((key) => serials.has(key)),
          map(() => undefined),
        ),
        this.resyncSubject,
      )
        .pipe(throttleTime(this.refreshThrottleMs, asyncScheduler, { leading: true, trailing: true }))
        .subscribe(subscriber);

      return () => {
        subscription.unsubscribe();
        this.removeDeviceWatches(serials);
      };
    });

    return this.whileConnected(updates);
  }

  private deviceKey(serialNumber: string): string {
    return serialNumber.trim().toUpperCase();
  }

  // Ties the lifetime of the hub connection to the number of active subscriptions.
  private whileConnected<T>(source: Observable<T>): Observable<T> {
    return new Observable<T>((subscriber) => {
      this.consumers += 1;
      if (this.consumers === 1) {
        this.start();
      }

      const subscription = source.subscribe(subscriber);

      return () => {
        subscription.unsubscribe();
        this.consumers -= 1;
        if (this.consumers === 0) {
          this.stop();
        }
      };
    });
  }

  private start(): void {
    const connection = new HubConnectionBuilder()
      .withUrl(this.hubUrl, {
        // WebSockets only, without the negotiate round-trip: the session cookie authenticates
        // the handshake, and no sticky sessions are needed if the API ever runs several pods.
        skipNegotiation: true,
        transport: HttpTransportType.WebSockets,
        withCredentials: true,
      })
      // Never give up: back off to one attempt every 30s until the connection returns.
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: (context) => this.retryDelay(context.previousRetryCount),
      })
      .configureLogging(LogLevel.Warning)
      .build();

    connection.on('notificationsChanged', () => this.notificationsChangedSubject.next());
    connection.on('telemetryUpdated', (payload: { serialNumber?: string } | null) => {
      if (payload?.serialNumber) {
        this.telemetryUpdatedSubject.next(this.deviceKey(payload.serialNumber));
      }
    });
    connection.onreconnected(() => this.onConnected(connection, true));

    this.connection = connection;
    this.connect(connection, 0);
  }

  private connect(connection: HubConnection, attempt: number): void {
    connection
      .start()
      .then(() => this.onConnected(connection, attempt > 0))
      .catch(() => {
        // withAutomaticReconnect only covers a connection that was established once; the
        // very first attempt has to be retried by hand.
        if (this.connection !== connection) {
          return;
        }
        this.retryTimer = setTimeout(() => this.connect(connection, attempt + 1), this.retryDelay(attempt));
      });
  }

  private retryDelay(previousAttempts: number): number {
    return Math.min(this.maxRetryDelayMs, 2000 * 2 ** previousAttempts);
  }

  private onConnected(connection: HubConnection, mayHaveMissedEvents: boolean): void {
    if (this.connection !== connection) {
      return;
    }

    // Group membership lives on the server per connection, so it is rebuilt on every (re)connect.
    this.invoke('SubscribeToDevices', [...this.deviceWatches.values()].map((watch) => watch.serialNumber));

    if (mayHaveMissedEvents) {
      this.resyncSubject.next();
    }
  }

  private stop(): void {
    if (this.retryTimer) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }

    const connection = this.connection;
    this.connection = null;
    connection?.stop().catch(() => undefined);
  }

  private addDeviceWatches(serials: Map<string, string>): void {
    const added: string[] = [];
    serials.forEach((serialNumber, key) => {
      const existing = this.deviceWatches.get(key);
      if (existing) {
        existing.watchers += 1;
      } else {
        this.deviceWatches.set(key, { serialNumber, watchers: 1 });
        added.push(serialNumber);
      }
    });

    this.invoke('SubscribeToDevices', added);
  }

  private removeDeviceWatches(serials: Map<string, string>): void {
    const removed: string[] = [];
    serials.forEach((serialNumber, key) => {
      const existing = this.deviceWatches.get(key);
      if (!existing) {
        return;
      }

      existing.watchers -= 1;
      if (existing.watchers === 0) {
        this.deviceWatches.delete(key);
        removed.push(serialNumber);
      }
    });

    this.invoke('UnsubscribeFromDevices', removed);
  }

  // Only sends when connected; anything requested while offline is replayed by onConnected.
  private invoke(method: string, serialNumbers: string[]): void {
    if (serialNumbers.length === 0 || this.connection?.state !== HubConnectionState.Connected) {
      return;
    }

    this.connection.invoke(method, serialNumbers).catch(() => undefined);
  }
}
